import type { JSX } from "react";
import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { api, type Lot } from "../api.js";
import { useNumberFormat } from "../contexts/numberFormat/index.js";
import { PageHeader } from "../components/PageHeader.js";
import { PageActions } from "../components/PageActions.js";
import { Money } from "../components/Money.js";
import { csvMoney, downloadCsv } from "../lib/csv.js";
import { cn } from "../lib/utils.js";
import { DataTable, uniq, type Column } from "../components/data-table.js";

/** Format an IBKR lot open date ("2025-01-20" or "20250706;093002"). */
function formatLotDate(raw: string | null): string {
  if (!raw) return "—";
  let d = raw.split(";")[0] ?? raw;
  if (/^\d{8}$/.test(d)) d = `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
  const parsed = new Date(d);
  return Number.isNaN(parsed.getTime()) ? d : parsed.toLocaleDateString();
}

function lotMs(raw: string | null): number {
  if (!raw) return 0;
  let d = raw.split(";")[0] ?? raw;
  if (/^\d{8}$/.test(d)) d = `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
  const t = Date.parse(d);
  return Number.isNaN(t) ? 0 : t;
}

export function StocksSlots(): JSX.Element {
  const { formatNumber } = useNumberFormat();
  const lots = useQuery({ queryKey: ["lots"], queryFn: () => api.lots() });
  const rows = lots.data?.items ?? [];
  const lastUpdated = rows[0]?.observed_at ?? null;

  const exportCsv = (): void => {
    downloadCsv(
      "slots.csv",
      ["Symbol", "Name", "Account", "Broker", "Class", "Currency", "LotQty", "Bought", "Price", "MarketValue", "CostBasis", "ValueEUR", "UnrealizedPL_EUR"],
      rows.map((l) => [
        l.symbol, l.name ?? "", l.account_name, l.provider ?? "", l.asset_class, l.currency,
        l.quantity, formatLotDate(l.opened_at), csvMoney(l.mark_price_minor),
        csvMoney(l.mark_value_minor), csvMoney(l.cost_basis_minor), csvMoney(l.value_eur_minor),
        csvMoney(l.unrealized_pl_eur_minor),
      ]),
    );
  };

  const columns: Column<Lot>[] = [
    {
      key: "symbol",
      label: "Symbol",
      sort: (a, b) => a.symbol.localeCompare(b.symbol),
      filter: { kind: "search", accessor: (l) => `${l.symbol} ${l.name ?? ""}`, placeholder: "Symbol / name…" },
      cell: (l) => (
        <>
          <div className="font-medium">{l.symbol}</div>
          {l.name && <div className="text-muted-foreground text-xs">{l.name}</div>}
        </>
      ),
    },
    {
      key: "account",
      label: "Account",
      sort: (a, b) => a.account_name.localeCompare(b.account_name),
      filter: { kind: "select", accessor: (l) => l.account_name, options: (rs) => uniq(rs.map((l) => l.account_name)) },
      cell: (l) => (
        <>
          <div>{l.account_name}</div>
          {l.provider && <div className="text-muted-foreground text-xs">{l.provider}</div>}
        </>
      ),
    },
    {
      key: "class",
      label: "Class",
      sort: (a, b) => a.asset_class.localeCompare(b.asset_class),
      filter: { kind: "select", accessor: (l) => l.asset_class, options: (rs) => uniq(rs.map((l) => l.asset_class)) },
      cell: (l) => l.asset_class,
    },
    {
      key: "qty",
      label: "Lot Qty",
      align: "right",
      sort: (a, b) => Number(a.quantity) - Number(b.quantity),
      cell: (l) => formatNumber(Number(l.quantity)),
    },
    {
      key: "opened",
      label: "Bought",
      align: "right",
      sort: (a, b) => lotMs(a.opened_at) - lotMs(b.opened_at),
      cell: (l) => formatLotDate(l.opened_at),
    },
    {
      key: "price",
      label: "Price",
      align: "right",
      cell: (l) => (l.mark_price_minor != null ? <Money amountMinor={l.mark_price_minor} native={l.currency} /> : "—"),
    },
    {
      key: "value",
      label: "Value",
      align: "right",
      sort: (a, b) => a.value_eur_minor - b.value_eur_minor,
      cell: (l) => (
        <span className="font-medium">
          {l.mark_value_minor != null ? <Money amountMinor={l.mark_value_minor} native={l.currency} /> : "—"}
        </span>
      ),
    },
    {
      key: "cost",
      label: "Cost basis",
      align: "right",
      cell: (l) => (l.cost_basis_minor != null ? <Money amountMinor={l.cost_basis_minor} native={l.currency} /> : "—"),
    },
    {
      key: "pl",
      label: "Unrl. P&L",
      align: "right",
      sort: (a, b) => (a.unrealized_pl_eur_minor ?? 0) - (b.unrealized_pl_eur_minor ?? 0),
      cell: (l) => (
        <span className={cn((l.unrealized_pl_eur_minor ?? 0) < 0 ? "text-bad" : "text-good")}>
          {l.unrealized_pl_eur_minor != null ? <Money amountMinor={l.unrealized_pl_eur_minor} native="EUR" /> : "—"}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Slots">
        <PageActions
          updatedAt={lastUpdated}
          actions={[{ label: "Export CSV", icon: Download, onClick: exportCsv }]}
        />
      </PageHeader>
      <p className="text-muted-foreground mb-4 text-sm">
        Every open tax lot, un-netted — the raw broker rows behind each position.
      </p>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(l, i) => `${l.account_id}-${l.symbol}-${l.opened_at ?? ""}-${String(i)}`}
        defaultSort="value"
        loading={lots.isLoading}
        emptyText={
          rows.length === 0
            ? "No lots yet — they appear after the next broker sync."
            : "No lots match these filters."
        }
      />
    </>
  );
}
